import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import DashLayout from "@/components/DashLayout";
import FileUploader from "@/components/FileUploader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2, X, ArrowLeft, Save } from "lucide-react";
import { toast } from "sonner";

const empty = {
  name: "", city: "", state: "", address: "", locality: "", map_url: "",
  board: "CBSE", school_type: "Sr. Secondary", classes_offered: "", student_faculty_ratio: "",
  description: "", logo_url: "", feature_image_url: "", gallery: [], brochures: [], facilities: [],
  fees_structure: "", fees_min: 0, fees_max: 0, monthly_fee_nursery: 0,
  establishment_year: 2000, size_acres: 0,
  featured: false, admission_open: false, verified: false, admission_partner: false,
  contact_email: "", contact_phone: "", website: "", slug: "", code: "",
  seo_title: "", meta_description: "",
};

export default function SuperSchoolForm() {
  const { id } = useParams();
  const nav = useNavigate();
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!id) return;
    api.get(`/schools/${id}`).then((r) => {
      const s = r.data;
      setF({ ...empty, ...s, gallery: s.gallery || [], brochures: s.brochures || [], facilities: s.facilities || [] });
    });
  }, [id]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...f,
        facilities: typeof f.facilities === "string" ? f.facilities.split(",").map(x => x.trim()).filter(Boolean) : f.facilities,
        fees_min: Number(f.fees_min) || 0, fees_max: Number(f.fees_max) || 0,
        monthly_fee_nursery: Number(f.monthly_fee_nursery) || 0,
        establishment_year: Number(f.establishment_year) || 2000, size_acres: Number(f.size_acres) || 0,
      };
      if (id) await api.put(`/schools/${id}`, payload);
      else await api.post("/schools", payload);
      toast.success(id ? "Updated" : "Created");
      nav("/super/schools");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const removeUrl = (key, url) => setF({ ...f, [key]: (f[key] || []).filter((x) => x !== url) });
  const appendUrls = (key, urls) => setF({ ...f, [key]: [...(f[key] || []), ...(Array.isArray(urls) ? urls : [urls])] });

  return (
    <DashLayout variant="super" title={id ? "Edit School" : "Add School"}>
      <Button variant="ghost" size="sm" onClick={() => nav("/super/schools")} className="mb-3" data-testid="ssf-back"><ArrowLeft className="w-4 h-4 mr-1" /> Back to Schools</Button>
      <form onSubmit={submit} className="space-y-4" data-testid="school-form">
        <Card className="p-4 grid md:grid-cols-2 gap-3">
          {[
            ["code", "School Code", "text"],
            ["name", "Name *", "text", true],
            ["slug", "Slug", "text"],
            ["locality", "Locality / Area", "text"],
            ["city", "City *", "text", true],
            ["state", "State", "text"],
            ["address", "Address", "text"],
            ["map_url", "Google Maps URL", "url"],
            ["classes_offered", "Classes Offered", "text"],
            ["student_faculty_ratio", "Student : Faculty ratio", "text"],
            ["monthly_fee_nursery", "Monthly Fee — Nursery (₹)", "number"],
            ["fees_structure", "Fees Structure (text)", "text"],
            ["fees_min", "Fees Min (₹/yr)", "number"],
            ["fees_max", "Fees Max (₹/yr)", "number"],
            ["establishment_year", "Establishment Year", "number"],
            ["size_acres", "Size (acres)", "number"],
            ["contact_email", "Contact Email", "email"],
            ["contact_phone", "Contact Phone", "text"],
            ["website", "Website", "url"],
          ].map(([k, l, t, req]) => (
            <div key={k}><Label className="mb-1 block text-xs">{l}
              {k === "code" && <span className="text-[10px] text-slate-400 font-normal ml-1">(leave blank to auto-generate)</span>}
            </Label>
              <Input type={t} required={req} value={f[k] ?? ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`sf-${k}`} /></div>
          ))}
          <div><Label className="mb-1 block text-xs">Board</Label>
            <Select value={f.board} onValueChange={(v) => setF({ ...f, board: v })}>
              <SelectTrigger data-testid="sf-board"><SelectValue /></SelectTrigger>
              <SelectContent>{["CBSE","ICSE","State","IB"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label className="mb-1 block text-xs">School Type</Label>
            <Select value={f.school_type} onValueChange={(v) => setF({ ...f, school_type: v })}>
              <SelectTrigger data-testid="sf-type"><SelectValue /></SelectTrigger>
              <SelectContent>{["Pre","Primary","Middle","Secondary","Sr. Secondary"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="md:col-span-2"><Label className="mb-1 block text-xs">Description</Label>
            <Textarea rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="sf-description" /></div>
          <div className="md:col-span-2"><Label className="mb-1 block text-xs">Facilities (comma separated)</Label>
            <Input value={Array.isArray(f.facilities) ? f.facilities.join(", ") : f.facilities} onChange={(e) => setF({ ...f, facilities: e.target.value })} data-testid="sf-facilities" /></div>
        </Card>

        <Card className="p-4 space-y-4">
          <h3 className="font-bold text-sm">Images & Files</h3>

          <div>
            <Label className="mb-1 block text-xs">Logo (image · max 100 KB)</Label>
            <div className="flex items-center gap-3">
              {f.logo_url && <img src={f.logo_url} alt="logo" className="w-14 h-14 rounded border object-contain bg-white" data-testid="sf-logo-preview" />}
              <FileUploader folder="schools/logos" label="Upload Logo" testId="sf-logo-upload" maxKb={100} accept="image/*" onUploaded={(url) => setF({ ...f, logo_url: url })} />
              <Input placeholder="or paste URL" value={f.logo_url} onChange={(e) => setF({ ...f, logo_url: e.target.value })} className="flex-1" data-testid="sf-logo-url" />
              {f.logo_url && <Button type="button" variant="ghost" size="sm" onClick={() => setF({ ...f, logo_url: "" })} data-testid="sf-logo-clear"><X className="w-4 h-4" /></Button>}
            </div>
          </div>

          <div>
            <Label className="mb-1 block text-xs">Feature Image (image · max 100 KB)</Label>
            <div className="flex items-center gap-3">
              {f.feature_image_url && <img src={f.feature_image_url} alt="feature" className="w-24 h-16 rounded border object-cover" data-testid="sf-feature-preview" />}
              <FileUploader folder="schools/features" label="Upload Feature Image" testId="sf-feature-upload" maxKb={100} accept="image/*" onUploaded={(url) => setF({ ...f, feature_image_url: url })} />
              <Input placeholder="or paste URL" value={f.feature_image_url} onChange={(e) => setF({ ...f, feature_image_url: e.target.value })} className="flex-1" data-testid="sf-feature-url" />
              {f.feature_image_url && <Button type="button" variant="ghost" size="sm" onClick={() => setF({ ...f, feature_image_url: "" })}><X className="w-4 h-4" /></Button>}
            </div>
          </div>

          <div>
            <Label className="mb-1 block text-xs">Gallery (images · max 100 KB each)</Label>
            <FileUploader folder="schools/gallery" label="Add Gallery Images" testId="sf-gallery-upload" maxKb={100} accept="image/*" multiple onUploaded={(urls) => appendUrls("gallery", urls)} />
            <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mt-3" data-testid="sf-gallery-preview">
              {(f.gallery || []).map((u, i) => (
                <div key={i} className="relative group">
                  <img src={u} alt={`g-${i}`} className="w-full h-20 object-cover rounded border" />
                  <button type="button" onClick={() => removeUrl("gallery", u)} className="absolute top-1 right-1 bg-black/60 text-white rounded p-1 opacity-0 group-hover:opacity-100 transition" data-testid={`sf-gallery-remove-${i}`}><Trash2 className="w-3 h-3" /></button>
                </div>
              ))}
              {(f.gallery || []).length === 0 && <div className="col-span-6 text-xs text-slate-400 border border-dashed border-slate-300 rounded p-4 text-center">No gallery images yet</div>}
            </div>
          </div>

          <div>
            <Label className="mb-1 block text-xs">Brochures (PDF/image · max 250 KB each)</Label>
            <FileUploader folder="schools/brochures" label="Add Brochure" testId="sf-brochure-upload" maxKb={250} accept="image/*,application/pdf" multiple onUploaded={(urls) => appendUrls("brochures", urls)} />
            <div className="mt-3 space-y-1" data-testid="sf-brochures-preview">
              {(f.brochures || []).map((u, i) => (
                <div key={i} className="flex items-center gap-2 text-xs bg-slate-50 rounded px-3 py-2">
                  <a href={u} target="_blank" rel="noreferrer" className="text-blue-700 underline truncate flex-1">{u.split("/").pop()}</a>
                  <Button type="button" variant="ghost" size="sm" onClick={() => removeUrl("brochures", u)} data-testid={`sf-brochure-remove-${i}`}><Trash2 className="w-3.5 h-3.5 text-red-600" /></Button>
                </div>
              ))}
              {(f.brochures || []).length === 0 && <div className="text-xs text-slate-400 border border-dashed border-slate-300 rounded p-4 text-center">No brochures uploaded</div>}
            </div>
          </div>
        </Card>

        <Card className="p-4 grid md:grid-cols-2 gap-3">
          <div className="flex items-center gap-3"><Switch checked={f.featured} onCheckedChange={(v) => setF({ ...f, featured: v })} data-testid="sf-featured" /><Label>Featured Listing</Label></div>
          <div className="flex items-center gap-3"><Switch checked={f.admission_open} onCheckedChange={(v) => setF({ ...f, admission_open: v })} data-testid="sf-admission-open" /><Label>Admission Open</Label></div>
          <div className="flex items-center gap-3"><Switch checked={f.verified} onCheckedChange={(v) => setF({ ...f, verified: v })} data-testid="sf-verified" /><Label>Verified by School</Label></div>
          <div className="flex items-center gap-3"><Switch checked={f.admission_partner} onCheckedChange={(v) => setF({ ...f, admission_partner: v })} data-testid="sf-admission-partner" /><Label>Official Admission Partner</Label></div>
        </Card>

        <Card className="p-4">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">SEO (browser tab & search results)</div>
          <div className="grid md:grid-cols-2 gap-3">
            <div><Label className="mb-1 block text-xs">Page Title (SEO)</Label>
              <Input value={f.seo_title} onChange={(e) => setF({ ...f, seo_title: e.target.value })} placeholder={`e.g. ${f.name || "School Name"} in ${f.city || "City"} — Fees, Reviews, Admission`} data-testid="sf-seo-title" />
            </div>
            <div><Label className="mb-1 block text-xs">Meta Description (SEO)</Label>
              <Textarea rows={2} value={f.meta_description} onChange={(e) => setF({ ...f, meta_description: e.target.value })} placeholder="1-2 sentences shown in Google search results" data-testid="sf-meta-desc" />
            </div>
          </div>
        </Card>

        <div className="flex gap-2 sticky bottom-0 bg-white/90 backdrop-blur border-t border-slate-200 py-3">
          <Button type="submit" disabled={busy} className="bg-slate-900 hover:bg-slate-800" data-testid="sf-submit"><Save className="w-4 h-4 mr-1" /> {busy ? "Saving…" : (id ? "Update School" : "Create School")}</Button>
          <Button type="button" variant="outline" onClick={() => nav("/super/schools")}>Cancel</Button>
        </div>
      </form>
    </DashLayout>
  );
}
